import { Component, Inject, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { SelectActionComponent } from 'src/app/shared/dialog/select-action/select-action.component';
import { SubSink } from 'subsink';
import { ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';
import { base64ToFile } from 'src/app/shared/utils';
import { DocumentRequest } from 'src/app/shared/models/document-request.model';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { RegistrationService } from '../shared/registration.service';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { NotificationStateModel, RegistrationState } from '../state/registration.state';
import { Store } from '@ngxs/store';
import { forkJoin, of } from 'rxjs';
import { DownloadDocument } from 'src/app/shared/models/download-document.model';

const UPLOAD_TYPE_ERROR = 'Upload mp4, jpeg, jpg or png files';
const ISO_8601 = 'YYYY-MM-DDTHH:mm:ss[Z]';
const ERROR_MESSAGE = 'Something Went Wrong.. please try again later';
const DELETE_MESSAGE = 'Are you sure you want to delete this document? This action cannot be undone.';
const DELETED_DOCUMENT = 'Document deleted successfully';

@Component({
    selector: 'app-vehicle-images',
    templateUrl: './vehicle-images.component.html',
    styleUrls: ['./vehicle-images.component.scss'],
    standalone: false
})
export class VehicleImagesComponent implements OnInit {
  form: UntypedFormGroup;
  acceptTypesPicture = ['image/png', 'image/jpg', 'image/jpeg'];
  acceptTypesMultiple = ['image/png', 'image/jpg', 'image/jpeg'];
  //'video/mp4', 'video/webm', 'video/ogg'
  uploadError: string;
  previewImage: null;
  isUploading: boolean = false;
  subSink = new SubSink();
  isRequested: boolean = false;
  isExpiring: boolean = true;
  isCarrierRequested = true;
  imageChangedEvent: any;
  canvasRotation = 0;
  transform: ImageTransform = {};
  documentPicture: any;
  showCrop = false;
  loading = false;
  uploadedFile: any;
  allVehicleImages: any[] = [];
  allImageUrls: any[] = [];
  

  constructor(
    private readonly location: Location,
    @Inject('environmentData') public environment: any,
    private readonly bottomSheet: MatBottomSheet,
    private readonly dateTimeService: DateTimeService,
    private readonly registrationService: RegistrationService,
    private readonly toastService: ToastService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly store: Store
  ) {}

  ngOnInit(): void {
    this.loadDriverDocuments();
    this.form = this.formBuilder.group({
      file: ['', [Validators.required]]
    });
    this.subSink.sink = this.form.get('file').valueChanges.subscribe(value => {
      if (value?.id) {
        this.uploadedFile = { ...value };
        this.isUploading = false;
      } else {
        this.uploadedFile = null;
      }
    });
  }

  loadDriverDocuments(): void {
    this.subSink.add(
      this.registrationService
        .loadDriver()
        .pipe(map(() => this.store.selectSnapshot(RegistrationState.notification)))
        .subscribe((notificationStateModel: NotificationStateModel) => {
          if (notificationStateModel?.notifications) {
            this.allVehicleImages = notificationStateModel.notifications.filter(
              doc => doc.type === 'VehicleImage' && doc.status === 'uploaded'
            ) || [];
            
            if (this.allVehicleImages.length > 0) {
              const downloadRequests = this.allVehicleImages.map(doc =>
                this.registrationService.downloadDocument(doc.id)
              );

              forkJoin(downloadRequests).subscribe((responses: any[]) => {
                this.allImageUrls = this.allVehicleImages.map((doc, i) => {
                  const fileUrl = responses[i].fileUrl;
                  const mimeType = responses[i].mimeType || this.getMimeTypeFromUrl(fileUrl);
                  const docId = doc.id;

                  return {
                    docId,
                    fileUrl,
                    mimeType
                  };
                });
              });
            } else {
              this.allImageUrls = [];
            }
          } else {
            this.allVehicleImages = [];
            this.allImageUrls = [];
          }
        })
    );
  }

  onDownloadFileClick(id: string) {
    this.registrationService.downloadDocument(id).subscribe(response => {
      this.downloadDocument(response);
    });
  }
  downloadDocument(document: DownloadDocument) {
    if (document && document.fileUrl) {
      window.open(document.fileUrl);
    }
  }

  deleteDocument(id: string): void {
    if (confirm(DELETE_MESSAGE)) {
      this.registrationService
        .deleteDocument(id)
        .pipe(
          catchError(() => {
            this.toastService.showError(ERROR_MESSAGE);
            return of(false);
          })
        )
        .subscribe(() => {
          this.toastService.showSuccess(DELETED_DOCUMENT);
          this.backAction();
        });
    }
  }

  getMimeTypeFromUrl(url: string): string {
    try {
      const cleanUrl = url.split('?')[0];
      const extension = cleanUrl
        .split('.')
        .pop()
        ?.toLowerCase();

      if (!extension) return 'unknown';
      const mimeMap: { [key: string]: string } = {
        jpg: 'image',
        jpeg: 'image',
        png: 'image',
        gif: 'image',
        webp: 'image',
        mp4: 'video',
        webm: 'video',
        ogg: 'video'
      };
      return mimeMap[extension] || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  backAction() {
    this.location.back();
  }

  openActionSheet(pictureSingle, uploadMultiple): void {
    if (this.environment?.featureFlags?.enableDQFDocImageCropFeature) {
      let options: any = [
        {
          displayName: 'Take Picture',
          name: 'picture'
        },
        {
          displayName: 'Upload Image',
          name: 'upload'
        }
      ];
      this.bottomSheet.open(SelectActionComponent, {
        data: {
          title: 'Select Action',
          options: options,
          selectedValue: null,
          onConfirm: (val: string) => {
            if (val === 'picture') {
              this.onFileInputClicked(pictureSingle);
            } else {
              this.onFileInputClicked(uploadMultiple);
            }
          }
        }
      });
    } else {
      this.onFileInputClicked(uploadMultiple);
    }
  }

  onFileInputClicked(fileInput: any) {
    fileInput.click();
  }

  onUploadFileChange($event: any): void {
    this.isUploading = true;
    this.uploadError = null;
    if (!$event || $event.target.files.length === 0) {
      return;
    }
    const [file] = $event.currentTarget.files;
    if (!this.acceptTypesMultiple.includes(file.type)) {
      this.uploadError = UPLOAD_TYPE_ERROR;
      this.isUploading = false;
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      this.form.get('file').patchValue(file);
      $event.target.files = null;
      $event.target.value = null;
      this.uploadDocument();
    };
  }

  onProcessPictureFile(event: any) {
    if (event.target.files && event.target.files.length) {
      const reader = new FileReader();
      const [file] = event.target.files;
      reader.readAsDataURL(file);

      reader.onload = () => {
        this.documentPicture = file;
      };
      this.loading = true;
      this.showCrop = true;
      this.imageChangedEvent = event;
    }
  }

  onImageCropped(event: ImageCroppedEvent) {
    if (event && event.base64) {
      const imageFile = new File([base64ToFile(event.base64)], this.documentPicture.name, {
        type: this.documentPicture.type
      });
      this.documentPicture = imageFile;
    }
  }

  onImageLoaded() {
    this.loading = false;
  }

  onCropperReady() {
    this.loading = false;
  }

  rotateLeft() {
    this.canvasRotation--;
    this.flipAfterRotate();
  }
  rotateRight() {
    this.canvasRotation++;
    this.flipAfterRotate();
  }

  private flipAfterRotate() {
    const flippedH = this.transform.flipH;
    const flippedV = this.transform.flipV;
    this.transform = {
      ...this.transform,
      flipH: flippedV,
      flipV: flippedH
    };
  }

  onFinishCrop() {
    this.form.get('file').patchValue(this.documentPicture);
    this.uploadDocument();
  }

  onCancelCrop() {
    this.previewImage = null;
    this.documentPicture = null;
    this.showCrop = false;
    this.loading = false;
  }

  onLoadImageFailed() {
    this.loading = false;
  }

  uploadDocument(): void {
    this.loading = true;
    const model = this.prepareSaveModel(this.form.value);
    if (model === undefined) {
      return;
    }
    this.registrationService
      .saveDocument(model, this.form.value.file)
      .pipe(
        tap(response => {
          this.toastService.showSuccess('Uploaded Successfully');
        }),
        catchError((error: string) => {
          this.toastService.showError('Something went wrong, Please try again later.');
          throw error;
        }),
        finalize(() => {
          this.loading = false;
          this.backAction();
        })
      )
      .subscribe(() => {});
  }

  private prepareSaveModel(formValue: any): DocumentRequest {
    const extension = formValue.file.name
      .split('.')
      .pop()
      .toLowerCase();
    const formModel = formValue;

    if (['pdf', 'jpeg', 'jpg', 'png', 'mp4', 'webm', 'ogg'].find(element => element == extension) == undefined) {
      return undefined;
    }

    let SOURCE_TEXT = '-';

    let model: any = {
      source: SOURCE_TEXT,
      extension: extension,
      generationDate: this.dateTimeService.formatDateTime(formModel.generationDate, ISO_8601),
      reportType: 'VehicleImage'
    };

    return model as DocumentRequest;
  }

}
